// components/dashboard/UsageAnalyticsWidget.js
import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calendar, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const UsageAnalyticsWidget = ({ api }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem('usageWidgetExpanded');
    return saved === null ? true : saved === 'true';
  });

useEffect(() => {
  fetchUsage();

  // Listen for real-time usage updates
  const handleUsageUpdate = () => {
    console.log('📊 Usage updated - refreshing widget');
    fetchUsage();
  };

  if (api.socket) {
    api.socket.on('usage_updated', handleUsageUpdate);
    api.socket.on('new_complaint', handleUsageUpdate);
    api.socket.on('import_complete', handleUsageUpdate);
  }

  return () => {
    if (api.socket) {
      api.socket.off('usage_updated', handleUsageUpdate);
      api.socket.off('new_complaint', handleUsageUpdate);
      api.socket.off('import_complete', handleUsageUpdate);
    }
  };
}, []);

  useEffect(() => {
    localStorage.setItem('usageWidgetExpanded', isExpanded.toString());
  }, [isExpanded]);

  const fetchUsage = async () => {
    try {
      const data = await api.getUsage();
      setUsage(data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!usage || usage.unlimited) {
    return null;
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="text-sm text-purple-600">
            {payload[0].value} tickets created
          </p>
        </div>
      );
    }
    return null;
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Collapsed one-line view
  if (!isExpanded) {
    return (
      <div className="bg-white rounded-lg border">
        <button
          onClick={toggleExpanded}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-4 flex-1">
            <h3 className="text-sm font-semibold text-gray-900">Ticket Usage</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {usage.used} / {usage.limit} used
              </span>
              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    usage.percentageUsed >= 90 ? 'bg-red-500' :
                    usage.percentageUsed >= 80 ? 'bg-orange-500' :
                    usage.percentageUsed >= 50 ? 'bg-amber-500' :
                    'bg-purple-500'
                  }`}
                  style={{ width: `${Math.min(usage.percentageUsed, 100)}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {Math.round(usage.percentageUsed)}%
              </span>
              {usage.projection?.avgDailyUsage > 0 && (
                <span className="text-xs text-gray-500">
                  ~{usage.projection.avgDailyUsage}/day
                </span>
              )}
            </div>
          </div>
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </button>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="bg-white rounded-lg border">
      <div className="p-6">
        <button
          onClick={toggleExpanded}
          className="w-full flex items-center justify-between mb-6 hover:opacity-80 transition-opacity"
        >
          <div className="flex items-center justify-between flex-1">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Ticket Usage</h3>
              <p className="text-sm text-gray-500 mt-1">
                {usage.used} of {usage.limit} tickets used this month
              </p>
            </div>
            
            <div className="text-right mr-4">
              <div className="text-2xl font-bold text-gray-900">
                {Math.round(usage.percentageUsed)}%
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {usage.remaining} remaining
              </div>
            </div>
          </div>
          <ChevronUp className="w-5 h-5 text-gray-400" />
        </button>

        {/* Usage Chart */}
        {usage.dailyUsage && usage.dailyUsage.length > 0 && (
          <div className="mb-6">
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={usage.dailyUsage}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  style={{ fontSize: '12px' }}
                />
                <YAxis style={{ fontSize: '12px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#9333ea" 
                  strokeWidth={2}
                  dot={{ fill: '#9333ea', r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-3 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-medium">Daily Average</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {usage.projection?.avgDailyUsage || 0}
            </div>
            <div className="text-xs text-gray-600">
              tickets per day
            </div>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs font-medium">Resets In</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {usage.daysUntilReset}
            </div>
            <div className="text-xs text-gray-600">
              days
            </div>
          </div>
        </div>

        {/* Projection Warning */}
        {usage.projection?.projectedOverage && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                Projected to exceed limit
              </p>
              <p className="text-xs text-amber-700 mt-1">
                At your current pace, you'll use ~{usage.projection.projectedTotal} tickets this month 
                (limit: {usage.limit}). Consider upgrading to avoid interruptions.
              </p>
              {usage.projection.daysUntilLimit && (
                <p className="text-xs text-amber-700 mt-1">
                  You'll reach your limit in approximately {usage.projection.daysUntilLimit} days.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Upgrade CTA for high usage */}
        {usage.percentageUsed >= 70 && usage.percentageUsed < 100 && (
          <button
            onClick={() => window.location.href = '/pricing'}
            className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
          >
            Upgrade to Avoid Limits
          </button>
        )}

        {/* Critical State CTA */}
        {usage.percentageUsed >= 100 && (
          <button
            onClick={() => window.location.href = '/pricing'}
            className="w-full mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm"
          >
            Upgrade Now to Continue
          </button>
        )}
      </div>
    </div>
  );
};

export default UsageAnalyticsWidget;